import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';

import { injectTranslations } from '@lang';
import { basketSelectors } from '@store/basket/selectors';
import { entitySelectors } from '@store/entity/selectors';

import SectionContainer from '@common/components/SectionContainer';
import { Routes } from '@common/constants';
import { analytics, formatBasketItems, formatPrice } from '@features/analytics';
import Basket from '@routes/Basket/components/Basket';
import Breadcrumbs, { Crumb } from '@routes/Basket/components/Breadcrumbs/index';
import {
  clearBasketErrors,
  createOrderFailure,
  createOrderRequest,
  createOrderSuccess,
  updateBasket,
} from '@store/basket/actions';
import { createGuest, signIn } from '@store/guest/actions';
import { withRouter } from 'shared-routing';
import Row from '../components/BasketRow';
import BASKET_UPDATE_TYPES from '../components/BasketUpdateTypes';

const BasketWrapper = styled(SectionContainer)`
  flex-grow: 1;
`;

class BasketContainer extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      activeIndex: 0,
    };

    this.goStepBack = this.goStepBack.bind(this);
    this.handleBasketRecalculate = this.handleBasketRecalculate.bind(this);
    this.handleBreadcrumbChange = this.handleBreadcrumbChange.bind(this);
  }

  componentDidMount() {
    analytics.track({
      event: 'view_cart',
      params: {
        currency: this.props.basketDetails.total.currencyCode,
        value: formatPrice(this.props.basketDetails.total.amount),
        items: formatBasketItems(this.props.basketDetails.items),
      },
    });
  }

  componentWillUnmount() {
    const { clearBasketErrors } = this.props;

    clearBasketErrors();
  }

  handleBasketRecalculate(type, itemId, quantity) {
    const {
      property,
      language,
      propertyId,
      basketDetails: { items, promoCode, id },
      updateBasket,
    } = this.props;

    let basketItems = [];
    const itemIndex = items.findIndex((item) => String(item.id) === String(itemId));

    if (type === BASKET_UPDATE_TYPES.QUANTITY) {
      basketItems = [
        ...items.slice(0, itemIndex),
        {
          ...items[itemIndex],
          quantity,
        },
        ...items.slice(itemIndex + 1, items.length),
      ];
    }

    if (type === BASKET_UPDATE_TYPES.REMOVE) {
      const itemToRemove = items[itemIndex];

      if (itemToRemove && itemToRemove.dynamicPackage === 'parent') {
        basketItems = items.filter((item) => {
          return String(item.id) !== String(itemId) && item.packageBasketItemId !== itemToRemove.id;
        });
      } else {
        basketItems = [...items.slice(0, itemIndex), ...items.slice(itemIndex + 1, items.length)];
      }
    }

    return updateBasket(
      propertyId,
      id,
      language,
      {
        // extracting only promocode, API does not accept the entire basket object
        promoCode,
        items: basketItems,
      },
      property.slug,
      property.timezone,
      true
    );
  }

  goStepBack(activeIndex) {
    const { activeIndex: currentIndex } = this.state;

    if (activeIndex < currentIndex) {
      this.setState({ activeIndex });
      this.measureCheckoutStepChange(activeIndex);
    }
  }

  handleBreadcrumbChange(activeIndex) {
    this.setState({ activeIndex });
    this.measureCheckoutStepChange(activeIndex);
  }

  render() {
    const {
      basketDetails,
      isBasketDetailsFetching,
      basketErrors,
      tablesPage,
      router,
      promoCodeFailure,
      property,
      t,
    } = this.props;

    const { activeIndex } = this.state;

    return (
      <BasketWrapper>
        <Row align="left" alignMd="left">
          <Breadcrumbs handleBreadcrumbChange={this.goStepBack} activeIndex={activeIndex}>
            <Crumb title={t('basket.breadcrumbs.basket')}>
              <Basket
                basketErrors={basketErrors}
                handleBasketRecalculate={this.handleBasketRecalculate}
                basketDetails={basketDetails}
                isBasketDetailsFetching={isBasketDetailsFetching}
                handleSubmit={() => {
                  analytics.track({
                    event: 'begin_checkout',
                    params: {
                      currency: basketDetails.total.currencyCode,
                      value: formatPrice(basketDetails.total.amount),
                      items: formatBasketItems(basketDetails.items),
                    },
                  });
                  router.push({ pathname: Routes.CHECKOUT_SIGNIN });
                }}
                propertyId={property.id}
                language={property.language}
                tablesPage={tablesPage}
                promoCodeFailure={promoCodeFailure}
                tablesDisableChildBookings={
                  property?.modulesExcludingDisabled?.tables?.features?.disableChildBookings
                    ?.enabled ?? false
                }
                isVouchersPaymentEnabled={!!property.voucherSettings?.isVoucherEnabled}
              />
            </Crumb>
          </Breadcrumbs>
        </Row>
      </BasketWrapper>
    );
  }
}

BasketContainer.propTypes = {
  isBasketDetailsFetching: PropTypes.bool,
  basketDetails: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    items: PropTypes.array,
  }).isRequired,
  propertyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  language: PropTypes.string.isRequired,
  updateBasket: PropTypes.func.isRequired,
  clearBasketErrors: PropTypes.func.isRequired,
  basketErrors: PropTypes.object,
  spaClassesPage: PropTypes.object,
  tablesPage: PropTypes.object,
  t: PropTypes.func.isRequired,
};

BasketContainer.defaultProps = {
  spaClassesPage: null,
  tablesPage: null,
};

const mapStateToProps = (state) => {
  const property = entitySelectors.getProperty(state);

  return {
    property,
    basketDetails: basketSelectors.getBasketDetails(state),
    isBasketDetailsFetching: basketSelectors.getIsBasketDetailsFetching(state),
    basketErrors: basketSelectors.getBasketErrors(state),
    tablesPage: entitySelectors.getTablesPage(state),
    promoCodeFailure: basketSelectors.promoCodeFailure(state),
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      clearBasketErrors,
      updateBasket,
      createOrderRequest,
      createOrderSuccess,
      createOrderFailure,
      signIn,
      createGuest,
    },
    dispatch
  );

BasketContainer.defaultProps = {
  isBasketDetailsFetching: false,
  basketErrors: null,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(injectTranslations(BasketContainer)));
